import type {
  CreateEventRequest,
  EventDetailResponse,
  EventStatus,
  EventSummaryResponse,
  PaginatedResponse,
  RegistrationResponse,
  UpdateEventRequest,
} from '@tabita/contracts';
import { apiRequest } from './api-client';

export interface ListEventsParams {
  readonly search?: string | undefined;
  readonly status?: EventStatus | undefined;
  readonly includeArchived?: boolean | undefined;
  readonly limit?: number | undefined;
}

export const eventsApi = {
  list(params: ListEventsParams = {}, signal?: AbortSignal) {
    return apiRequest<PaginatedResponse<EventSummaryResponse>>('/events', {
      query: { ...params },
      signal,
    });
  },

  get(id: string, signal?: AbortSignal) {
    return apiRequest<EventDetailResponse>(`/events/${id}`, { signal });
  },

  create(body: CreateEventRequest) {
    return apiRequest<EventDetailResponse>('/events', { method: 'POST', body });
  },

  update(id: string, body: UpdateEventRequest) {
    return apiRequest<EventDetailResponse>(`/events/${id}`, { method: 'PATCH', body });
  },

  publish(id: string) {
    return apiRequest<EventDetailResponse>(`/events/${id}/publish`, { method: 'POST' });
  },

  cancel(id: string, reason: string) {
    return apiRequest<EventDetailResponse>(`/events/${id}/cancel`, {
      method: 'POST',
      body: { reason },
    });
  },

  complete(id: string) {
    return apiRequest<EventDetailResponse>(`/events/${id}/complete`, { method: 'POST' });
  },

  /** There is no delete. An event's history is kept long after it happens. */
  archive(id: string, reason: string) {
    return apiRequest<EventDetailResponse>(`/events/${id}/archive`, {
      method: 'POST',
      body: { reason },
    });
  },

  restore(id: string) {
    return apiRequest<EventDetailResponse>(`/events/${id}/restore`, { method: 'POST' });
  },

  // --- Registrations ----------------------------------------------------------

  listRegistrations(id: string, signal?: AbortSignal) {
    return apiRequest<readonly RegistrationResponse[]>(`/events/${id}/registrations`, { signal });
  },

  register(id: string, personId: string) {
    return apiRequest<EventDetailResponse>(`/events/${id}/registrations`, {
      method: 'POST',
      body: { personId },
    });
  },

  /**
   * The registration is cancelled, not deleted — it keeps its dates so a
   * re-registration has a history. DELETE is the honest verb for the
   * caller's intent even though the storage is a soft cancel.
   */
  cancelRegistration(id: string, personId: string, reason?: string | null) {
    return apiRequest<EventDetailResponse>(`/events/${id}/registrations/${personId}`, {
      method: 'DELETE',
      body: { reason: reason ?? null },
    });
  },
};
